import { ActionType, createReducer } from "typesafe-actions";
import * as actions from "./briefForm.actions";
import { resetApiError } from "store/features/general";
import { BriefingFormModel } from "pages/BriefingForm/interfaces";
import { briefingFormDefaultValues } from "validation/components/BriefingForm/briefingFormRules";

export type BriefFormAction = ActionType<typeof actions>;

export type BriefFormState = Readonly<{
    hasError: boolean;
    isProcessing: boolean;
    materialSpecificationId?: number;
    submitted: boolean;
    briefForm: BriefingFormModel;
}>;

export const briefFormState: BriefFormState = {
    hasError: false,
    isProcessing: false,
    submitted: false,
    briefForm: { ...briefingFormDefaultValues },
};

const {
    uploadBriefFormAsset,
    removeBriefFormAsset,
    submitBriefForm,
    fetchBriefForm,
    uploadBriefFormAdditionalAssets,
} = actions;

export const briefFormReducer = createReducer<BriefFormState, BriefFormAction>(briefFormState)
    .handleAction(fetchBriefForm.request, (state) => {
        return {
            ...state,
            isProcessing: true,
        };
    })
    .handleAction(
        [
            uploadBriefFormAsset.request,
            submitBriefForm.request,
            removeBriefFormAsset.request,
            uploadBriefFormAdditionalAssets.request,
        ],
        (state, { payload }) => {
            return {
                ...state,
                isProcessing: true,
                briefForm: {
                    ...payload,
                    digitalBrief: {
                        ...state.briefForm.digitalBrief,
                        ...payload.digitalBrief,
                        assets: state.briefForm.digitalBrief.assets,
                    },
                },
            };
        }
    )
    .handleAction(uploadBriefFormAsset.success, (state, { payload: { index, fileName, id } }) => {
        return {
            ...state,
            briefForm: {
                ...state.briefForm,
                digitalBrief: {
                    ...state.briefForm.digitalBrief,
                    assets: [
                        ...state.briefForm.digitalBrief.assets.map((asset, currentIndex) => {
                            if (currentIndex === index) {
                                return {
                                    id,
                                    fileName,
                                    copyText: state.briefForm.digitalBrief.assets[currentIndex].copyText,
                                };
                            }

                            return asset;
                        }),
                    ],
                },
            },
            isProcessing: false,
        };
    })
    .handleAction(uploadBriefFormAdditionalAssets.success, (state, { payload }) => {
        return {
            ...state,
            briefForm: {
                ...state.briefForm,
                digitalBrief: {
                    ...state.briefForm.digitalBrief,
                    assets: [...state.briefForm.digitalBrief.assets, ...payload],
                },
            },
            isProcessing: false,
        };
    })
    .handleAction(removeBriefFormAsset.success, (state, { payload }) => {
        const { id, removeFromArray } = payload;

        return {
            ...state,
            briefForm: {
                ...state.briefForm,
                digitalBrief: {
                    ...state.briefForm.digitalBrief,
                    assets: removeFromArray
                        ? [...state.briefForm.digitalBrief.assets.filter((asset) => asset.id !== id)]
                        : [
                              ...state.briefForm.digitalBrief.assets.map((asset) => {
                                  if (asset.id === id) {
                                      return {
                                          id: null,
                                          fileName: null,
                                          copyText: "",
                                      };
                                  }

                                  return asset;
                              }),
                          ],
                },
            },
            isProcessing: false,
        };
    })
    .handleAction(submitBriefForm.success, (state) => {
        return {
            ...state,
            submitted: true,
            isProcessing: false,
        };
    })
    .handleAction(fetchBriefForm.success, (state, { payload }) => {
        if (payload === undefined) {
            return {
                ...state,
                isProcessing: false,
            };
        }

        return {
            ...state,
            isProcessing: false,
            briefForm: { ...payload },
        };
    })
    .handleAction(
        [
            uploadBriefFormAsset.failure,
            submitBriefForm.failure,
            fetchBriefForm.failure,
            uploadBriefFormAdditionalAssets.failure,
        ],
        (state) => {
            return {
                ...state,
                isProcessing: false,
                hasError: true,
            };
        }
    )
    .handleAction(resetApiError as never, (state) => {
        return {
            ...state,
            hasError: false,
        };
    });
